import SkTechApiClient from "./base/SkTechApiClient";

export type PaymentInitResponse =
  | { type: "html"; payload: string }
  | { type: "url"; payload: string }
  | { type: "error"; payload: string };

export default class PaymentApiClient extends SkTechApiClient {
  public getBalance = async (companyId: string): Promise<any> => {
    return await this.Get(`/api/Payment/balance/${companyId}`)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error("Failed to fetch balance.");
      })
      .catch(function (error) {
        console.error("Error fetching balance:", error);
        throw error;
      });
  };

  public getPaymentDate = async (companyId: string): Promise<any> => {
    return await this.Get(`/api/Payment/payment-date/${companyId}`)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error("Failed to fetch payment date.");
      })
      .catch(function (error) {
        console.error("Error fetching payment date:", error);
        throw error;
      });
  };

  public getTransactionHistory = async (companyId: string): Promise<any> => {
    return await this.Get(`/api/Payment/transactionHistory/${companyId}`)
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error("Failed to fetch transaction history.");
      })
      .catch(function (error) {
        console.error("Error fetching transaction history:", error);
        throw error;
      });
  };

  public CreatePaymentRequest = async (companyId?: string, formData?: any): Promise<PaymentInitResponse> => {
    const baseUrl = this.BASE_URL;
    try {
      const response = await this.Post(`/api/Payment/CreatePaymentRequest`, { companyId, formData, baseUrl });
      const data = response?.data;

      // 1) LiqPay returns raw HTML (string)
      if (typeof data === "string") {
        return { type: "html", payload: data };
      }

      // 2) Normalize different field names
      const type =
        (data?.type ?? data?.Type ?? null) &&
        String(data.type ?? data.Type).toLowerCase();

      const payload =
        data?.payload ??
        data?.Payload ??
        data?.formHtml ??
        data?.FormHtml ??
        data?.html ??
        data?.HTML ??
        data?.url ??
        data?.Url ??
        null;

      if (type && payload) {
        // if type is not "html"|"url", we try to decontextualize
        const t = (type === "html" || type === "url")
          ? type
          : (data?.url || data?.Url ? "url" : "html");
        return { type: t as "html" | "url", payload: String(payload) };
      }

      // 3) Lego variants without type:
      if (data?.formHtml || data?.FormHtml || data?.html || data?.HTML) {
        return { type: "html", payload: String(data.formHtml ?? data.FormHtml ?? data.html ?? data.HTML) };
      }
      if (data?.url || data?.Url) {
        return { type: "url", payload: String(data.url ?? data.Url) };
      }

      // 4) Backend error case
      if (data?.isSuccess === false && data?.message) {
        return { type: "error", payload: String(data.message) };
      }

      return { type: "error", payload: "Unknown payment response format." };
    } catch (e) {
      console.error("Error creating payment request:", e);
      return { type: "error", payload: "Failed to create payment request." };
    }
  };

  public changeTariffPlan = async (companyId: string, newTariffPlanId: number, interval: string): Promise<any> => {
    return await this.Post(`/api/Payment/changeTariffPlan`, { companyId, newTariffPlanId, interval })
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error("Failed to change tariff plan.");
      })
      .catch(function (error) {
        console.error("Error changing tariff plan:", error);
        throw error;
      });
  };

}
